import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EAS_FEE_SLICE_NAME } from './constants';
import {
	TAnnualFeeTypeDetails,
	TAnnualFeeTypeListResponse,
	TAnnualFeeTypeOption,
	TCreateOrUpdateAnnualFeeTypeRequest,
	TCreateOrUpdateFeeClassRequest,
	TCreateOrUpdateInvoicingFeeTypeRequest,
	TCreateOrUpdateMonthlyFeeRequest,
	TEasFeeIdRequest,
	TEasFeeManagementRequest,
	TEasFeeStore,
	TFeeClassDetails,
	TFeeClassListResponse,
	TFeeClassOption,
	TInvoicingFeeTypeDetails,
	TInvoicingFeeTypeListResponse,
	TInvoicingFeeTypeOption,
	TMonthlyFeeDetails,
	TMonthlyFeeDetailsListResponse,
	TMonthlyFeeOption,
	TUpdateAnnualFeeTypeRequest,
	TUpdateFeeClassRequest,
	TUpdateInvoicingFeeTypeRequest,
	TUpdateMonthlyFeeRequest,
} from './types';

// ==========================================:
export const initialState: TEasFeeStore = {
	annualFeeTypeOptions: null,
	annualFeeTypeOptionsLoading: false,
	annualFeeTypeList: null,
	annualFeeTypeListLoading: false,
	annualFeeTypeLastListParams: null,
	annualFeeTypeDetails: null,
	annualFeeTypeDetailsLoading: false,
	annualFeeTypeSaveLoading: false,
	annualFeeTypeSaveSuccess: false,
	annualFeeTypeDeleteLoading: false,

	monthlyFeeOptions: null,
	monthlyFeeOptionsLoading: false,
	monthlyFeeList: null,
	monthlyFeeListLoading: false,
	monthlyFeeLastListParams: null,
	monthlyFeeDetails: null,
	monthlyFeeDetailsLoading: false,
	monthlyFeeSaveLoading: false,
	monthlyFeeSaveSuccess: false,
	monthlyFeeDeleteLoading: false,

	invoicingFeeTypeOptions: null,
	invoicingFeeTypeOptionsLoading: false,
	invoicingFeeTypeList: null,
	invoicingFeeTypeListLoading: false,
	invoicingFeeTypeLastListParams: null,
	invoicingFeeTypeDetails: null,
	invoicingFeeTypeDetailsLoading: false,
	invoicingFeeTypeSaveLoading: false,
	invoicingFeeTypeSaveSuccess: false,

	feeClassOptions: null,
	feeClassOptionsLoading: false,
	feeClassList: null,
	feeClassListLoading: false,
	feeClassLastListParams: null,
	feeClassDetails: null,
	feeClassDetailsLoading: false,
	feeClassSaveLoading: false,
	feeClassSaveSuccess: false,
	feeClassDeleteLoading: false,
};

// ==========================================:
const easFeeSlice = createSlice({
	name: EAS_FEE_SLICE_NAME,
	initialState,
	reducers: {
		// ============ Annual fee types ============
		getAnnualFeeTypeOptionsRequest: (state) => {
			state.annualFeeTypeOptionsLoading = true;
		},
		getAnnualFeeTypeOptionsSuccess: (state, action: PayloadAction<TAnnualFeeTypeOption[]>) => {
			state.annualFeeTypeOptions = action.payload;
			state.annualFeeTypeOptionsLoading = false;
		},
		getAnnualFeeTypeOptionsFailure: (state) => {
			state.annualFeeTypeOptionsLoading = false;
		},

		getAnnualFeeTypeListRequest: (state, action: PayloadAction<TEasFeeManagementRequest>) => {
			state.annualFeeTypeListLoading = true;
			state.annualFeeTypeLastListParams = action.payload;
		},
		getAnnualFeeTypeListSuccess: (state, action: PayloadAction<TAnnualFeeTypeListResponse>) => {
			state.annualFeeTypeList = action.payload;
			state.annualFeeTypeListLoading = false;
		},
		getAnnualFeeTypeListFailure: (state) => {
			state.annualFeeTypeListLoading = false;
		},

		getAnnualFeeTypeDetailsRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.annualFeeTypeDetailsLoading = true;
		},
		getAnnualFeeTypeDetailsSuccess: (state, action: PayloadAction<TAnnualFeeTypeDetails>) => {
			state.annualFeeTypeDetails = action.payload;
			state.annualFeeTypeDetailsLoading = false;
		},
		getAnnualFeeTypeDetailsFailure: (state) => {
			state.annualFeeTypeDetailsLoading = false;
		},
		clearAnnualFeeTypeDetails: (state) => {
			state.annualFeeTypeDetails = null;
		},

		createAnnualFeeTypeRequest: (
			state,
			_action: PayloadAction<TCreateOrUpdateAnnualFeeTypeRequest>,
		) => {
			state.annualFeeTypeSaveLoading = true;
			state.annualFeeTypeSaveSuccess = false;
		},
		updateAnnualFeeTypeRequest: (state, _action: PayloadAction<TUpdateAnnualFeeTypeRequest>) => {
			state.annualFeeTypeSaveLoading = true;
			state.annualFeeTypeSaveSuccess = false;
		},
		saveAnnualFeeTypeSuccess: (state) => {
			state.annualFeeTypeSaveLoading = false;
			state.annualFeeTypeSaveSuccess = true;
		},
		saveAnnualFeeTypeFailure: (state) => {
			state.annualFeeTypeSaveLoading = false;
		},
		resetSaveAnnualFeeTypeSuccess: (state) => {
			state.annualFeeTypeSaveSuccess = false;
		},

		deleteAnnualFeeTypeRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.annualFeeTypeDeleteLoading = true;
		},
		deleteAnnualFeeTypeSuccess: (state) => {
			state.annualFeeTypeDeleteLoading = false;
		},
		deleteAnnualFeeTypeFailure: (state) => {
			state.annualFeeTypeDeleteLoading = false;
		},

		// ============ Monthly fees ============
		getMonthlyFeeOptionsRequest: (state) => {
			state.monthlyFeeOptionsLoading = true;
		},
		getMonthlyFeeOptionsSuccess: (state, action: PayloadAction<TMonthlyFeeOption[]>) => {
			state.monthlyFeeOptions = action.payload;
			state.monthlyFeeOptionsLoading = false;
		},
		getMonthlyFeeOptionsFailure: (state) => {
			state.monthlyFeeOptionsLoading = false;
		},

		getMonthlyFeeListRequest: (state, action: PayloadAction<TEasFeeManagementRequest>) => {
			state.monthlyFeeListLoading = true;
			state.monthlyFeeLastListParams = action.payload;
		},
		getMonthlyFeeListSuccess: (state, action: PayloadAction<TMonthlyFeeDetailsListResponse>) => {
			state.monthlyFeeList = action.payload;
			state.monthlyFeeListLoading = false;
		},
		getMonthlyFeeListFailure: (state) => {
			state.monthlyFeeListLoading = false;
		},

		getMonthlyFeeDetailsRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.monthlyFeeDetailsLoading = true;
		},
		getMonthlyFeeDetailsSuccess: (state, action: PayloadAction<TMonthlyFeeDetails>) => {
			state.monthlyFeeDetails = action.payload;
			state.monthlyFeeDetailsLoading = false;
		},
		getMonthlyFeeDetailsFailure: (state) => {
			state.monthlyFeeDetailsLoading = false;
		},
		clearMonthlyFeeDetails: (state) => {
			state.monthlyFeeDetails = null;
		},

		createMonthlyFeeRequest: (state, _action: PayloadAction<TCreateOrUpdateMonthlyFeeRequest>) => {
			state.monthlyFeeSaveLoading = true;
			state.monthlyFeeSaveSuccess = false;
		},
		updateMonthlyFeeRequest: (state, _action: PayloadAction<TUpdateMonthlyFeeRequest>) => {
			state.monthlyFeeSaveLoading = true;
			state.monthlyFeeSaveSuccess = false;
		},
		saveMonthlyFeeSuccess: (state) => {
			state.monthlyFeeSaveLoading = false;
			state.monthlyFeeSaveSuccess = true;
		},
		saveMonthlyFeeFailure: (state) => {
			state.monthlyFeeSaveLoading = false;
		},
		resetSaveMonthlyFeeSuccess: (state) => {
			state.monthlyFeeSaveSuccess = false;
		},

		deleteMonthlyFeeRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.monthlyFeeDeleteLoading = true;
		},
		deleteMonthlyFeeSuccess: (state) => {
			state.monthlyFeeDeleteLoading = false;
		},
		deleteMonthlyFeeFailure: (state) => {
			state.monthlyFeeDeleteLoading = false;
		},

		// ============ Invoicing fee types ============
		getInvoicingFeeTypeOptionsRequest: (state) => {
			state.invoicingFeeTypeOptionsLoading = true;
		},
		getInvoicingFeeTypeOptionsSuccess: (
			state,
			action: PayloadAction<TInvoicingFeeTypeOption[]>,
		) => {
			state.invoicingFeeTypeOptions = action.payload;
			state.invoicingFeeTypeOptionsLoading = false;
		},
		getInvoicingFeeTypeOptionsFailure: (state) => {
			state.invoicingFeeTypeOptionsLoading = false;
		},

		getInvoicingFeeTypeListRequest: (state, action: PayloadAction<TEasFeeManagementRequest>) => {
			state.invoicingFeeTypeListLoading = true;
			state.invoicingFeeTypeLastListParams = action.payload;
		},
		getInvoicingFeeTypeListSuccess: (
			state,
			action: PayloadAction<TInvoicingFeeTypeListResponse>,
		) => {
			state.invoicingFeeTypeList = action.payload;
			state.invoicingFeeTypeListLoading = false;
		},
		getInvoicingFeeTypeListFailure: (state) => {
			state.invoicingFeeTypeListLoading = false;
		},

		getInvoicingFeeTypeDetailsRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.invoicingFeeTypeDetailsLoading = true;
		},
		getInvoicingFeeTypeDetailsSuccess: (state, action: PayloadAction<TInvoicingFeeTypeDetails>) => {
			state.invoicingFeeTypeDetails = action.payload;
			state.invoicingFeeTypeDetailsLoading = false;
		},
		getInvoicingFeeTypeDetailsFailure: (state) => {
			state.invoicingFeeTypeDetailsLoading = false;
		},
		clearInvoicingFeeTypeDetails: (state) => {
			state.invoicingFeeTypeDetails = null;
		},

		createInvoicingFeeTypeRequest: (
			state,
			_action: PayloadAction<TCreateOrUpdateInvoicingFeeTypeRequest>,
		) => {
			state.invoicingFeeTypeSaveLoading = true;
			state.invoicingFeeTypeSaveSuccess = false;
		},
		updateInvoicingFeeTypeRequest: (
			state,
			_action: PayloadAction<TUpdateInvoicingFeeTypeRequest>,
		) => {
			state.invoicingFeeTypeSaveLoading = true;
			state.invoicingFeeTypeSaveSuccess = false;
		},
		saveInvoicingFeeTypeSuccess: (state) => {
			state.invoicingFeeTypeSaveLoading = false;
			state.invoicingFeeTypeSaveSuccess = true;
		},
		saveInvoicingFeeTypeFailure: (state) => {
			state.invoicingFeeTypeSaveLoading = false;
		},
		resetSaveInvoicingFeeTypeSuccess: (state) => {
			state.invoicingFeeTypeSaveSuccess = false;
		},

		// ============ Fee classes ============
		getFeeClassOptionsRequest: (state) => {
			state.feeClassOptionsLoading = true;
		},
		getFeeClassOptionsSuccess: (state, action: PayloadAction<TFeeClassOption[]>) => {
			state.feeClassOptions = action.payload;
			state.feeClassOptionsLoading = false;
		},
		getFeeClassOptionsFailure: (state) => {
			state.feeClassOptionsLoading = false;
		},

		getFeeClassListRequest: (state, action: PayloadAction<TEasFeeManagementRequest>) => {
			state.feeClassListLoading = true;
			state.feeClassLastListParams = action.payload;
		},
		getFeeClassListSuccess: (state, action: PayloadAction<TFeeClassListResponse>) => {
			state.feeClassList = action.payload;
			state.feeClassListLoading = false;
		},
		getFeeClassListFailure: (state) => {
			state.feeClassListLoading = false;
		},

		getFeeClassDetailsRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.feeClassDetailsLoading = true;
		},
		getFeeClassDetailsSuccess: (state, action: PayloadAction<TFeeClassDetails>) => {
			state.feeClassDetails = action.payload;
			state.feeClassDetailsLoading = false;
		},
		getFeeClassDetailsFailure: (state) => {
			state.feeClassDetailsLoading = false;
		},
		clearFeeClassDetails: (state) => {
			state.feeClassDetails = null;
		},

		createFeeClassRequest: (state, _action: PayloadAction<TCreateOrUpdateFeeClassRequest>) => {
			state.feeClassSaveLoading = true;
			state.feeClassSaveSuccess = false;
		},
		updateFeeClassRequest: (state, _action: PayloadAction<TUpdateFeeClassRequest>) => {
			state.feeClassSaveLoading = true;
			state.feeClassSaveSuccess = false;
		},
		saveFeeClassSuccess: (state) => {
			state.feeClassSaveLoading = false;
			state.feeClassSaveSuccess = true;
		},
		saveFeeClassFailure: (state) => {
			state.feeClassSaveLoading = false;
		},
		resetSaveFeeClassSuccess: (state) => {
			state.feeClassSaveSuccess = false;
		},

		deleteFeeClassRequest: (state, _action: PayloadAction<TEasFeeIdRequest>) => {
			state.feeClassDeleteLoading = true;
		},
		deleteFeeClassSuccess: (state) => {
			state.feeClassDeleteLoading = false;
		},
		deleteFeeClassFailure: (state) => {
			state.feeClassDeleteLoading = false;
		},

		easFeeInitState: () => initialState,
	},
});

export default easFeeSlice.reducer;
export const {
	getAnnualFeeTypeOptionsRequest,
	getAnnualFeeTypeOptionsSuccess,
	getAnnualFeeTypeOptionsFailure,
	getAnnualFeeTypeListRequest,
	getAnnualFeeTypeListSuccess,
	getAnnualFeeTypeListFailure,
	getAnnualFeeTypeDetailsRequest,
	getAnnualFeeTypeDetailsSuccess,
	getAnnualFeeTypeDetailsFailure,
	clearAnnualFeeTypeDetails,
	createAnnualFeeTypeRequest,
	updateAnnualFeeTypeRequest,
	saveAnnualFeeTypeSuccess,
	saveAnnualFeeTypeFailure,
	resetSaveAnnualFeeTypeSuccess,
	deleteAnnualFeeTypeRequest,
	deleteAnnualFeeTypeSuccess,
	deleteAnnualFeeTypeFailure,

	getMonthlyFeeOptionsRequest,
	getMonthlyFeeOptionsSuccess,
	getMonthlyFeeOptionsFailure,
	getMonthlyFeeListRequest,
	getMonthlyFeeListSuccess,
	getMonthlyFeeListFailure,
	getMonthlyFeeDetailsRequest,
	getMonthlyFeeDetailsSuccess,
	getMonthlyFeeDetailsFailure,
	clearMonthlyFeeDetails,
	createMonthlyFeeRequest,
	updateMonthlyFeeRequest,
	saveMonthlyFeeSuccess,
	saveMonthlyFeeFailure,
	resetSaveMonthlyFeeSuccess,
	deleteMonthlyFeeRequest,
	deleteMonthlyFeeSuccess,
	deleteMonthlyFeeFailure,

	getInvoicingFeeTypeOptionsRequest,
	getInvoicingFeeTypeOptionsSuccess,
	getInvoicingFeeTypeOptionsFailure,
	getInvoicingFeeTypeListRequest,
	getInvoicingFeeTypeListSuccess,
	getInvoicingFeeTypeListFailure,
	getInvoicingFeeTypeDetailsRequest,
	getInvoicingFeeTypeDetailsSuccess,
	getInvoicingFeeTypeDetailsFailure,
	clearInvoicingFeeTypeDetails,
	createInvoicingFeeTypeRequest,
	updateInvoicingFeeTypeRequest,
	saveInvoicingFeeTypeSuccess,
	saveInvoicingFeeTypeFailure,
	resetSaveInvoicingFeeTypeSuccess,

	getFeeClassOptionsRequest,
	getFeeClassOptionsSuccess,
	getFeeClassOptionsFailure,
	getFeeClassListRequest,
	getFeeClassListSuccess,
	getFeeClassListFailure,
	getFeeClassDetailsRequest,
	getFeeClassDetailsSuccess,
	getFeeClassDetailsFailure,
	clearFeeClassDetails,
	createFeeClassRequest,
	updateFeeClassRequest,
	saveFeeClassSuccess,
	saveFeeClassFailure,
	resetSaveFeeClassSuccess,
	deleteFeeClassRequest,
	deleteFeeClassSuccess,
	deleteFeeClassFailure,

	easFeeInitState,
} = easFeeSlice.actions;
